import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Modal,
  styled,
  TextField,
  Typography,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DIALOG_BOX_STYLE } from '../../_constants/styleConst';

type FileImportModalProps = {
  isOpen: boolean;
  acceptableFiles: string[];
  file?: File;
  /**
   * 0:ファイル選択中
   * 1:インポート中
   * 2:成功
   * 3:エラー
   * 4:差分なし
   */
  status: 0 | 1 | 2 | 3 | 4;
  setFile: React.Dispatch<React.SetStateAction<File | undefined>>;
  onClickOk: () => void;
  handleClose: () => void;
  errorMsg: string;
};

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const FileImportModal: React.FC<FileImportModalProps> = ({
  isOpen,
  acceptableFiles,
  file,
  status,
  setFile,
  onClickOk,
  handleClose,
  errorMsg,
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    /** インポート成功後、自動でモーダルを閉じる */
    if (status === 2 || status === 4) {
      setTimeout(() => {
        closeModal(true);
      }, 2000);
    }
  }, [status]);

  const onChangeFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFile(event.target.files ? event.target.files[0] : undefined);
  };

  const closeModal = (isClose: boolean) => {
    /** モーダル外クリックで閉じない */
    if (isClose === true) {
      handleClose();
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={closeModal}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
    >
      <Box sx={DIALOG_BOX_STYLE}>
        <Grid container>
          <Grid size={3} mb={4}>
            {/* タイトル */}
            <Typography variant="h6" component="h2">
              {t('LBL_IMPORT')}
            </Typography>
          </Grid>
          {/* ステータスメッセージ */}
          <Grid size={9}>
            {status === 2 && (
              <Typography variant="h6" component="h2" color="success">
                {t('MSG_IMPORT_SUCCESSFUL')}
              </Typography>
            )}
            {status === 3 && (
              <Typography variant="h6" component="h2" color="error">
                {errorMsg}
              </Typography>
            )}
            {status === 4 && (
              <Typography variant="h6" component="h2" color="textDisabled">
                {t('MSG_IMPORT_NO_DIFFERENCE')}
              </Typography>
            )}
          </Grid>
        </Grid>
        {/* ファイル選択 */}
        <Grid container spacing={2}>
          <Grid size={9}>
            <TextField
              fullWidth
              size="small"
              disabled
              placeholder={t('LBL_NO_FILES_SELECTED')}
              value={file ? file.name : ''}
            ></TextField>
          </Grid>
          <Grid size={3} textAlign={'center'}>
            <Button
              component="label"
              role={undefined}
              variant="contained"
              fullWidth
              sx={{ bgcolor: '#fff', color: '#000', textTransform: 'none' }}
              tabIndex={-1}
            >
              <FolderIcon color="primary" />
              <VisuallyHiddenInput
                type="file"
                accept={acceptableFiles.join(', ')}
                onChange={onChangeFile}
              />
            </Button>
          </Grid>
        </Grid>
        {/* フッターのボタン */}
        <Grid container spacing={2} mt={3}>
          <Grid size={4} />
          <Grid size={4}>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => closeModal(true)}
              disabled={status === 1}
              sx={{ height: '2.5rem' }}
            >
              {t('BTN_CANCEL')}
            </Button>
          </Grid>
          <Grid size={4}>
            <Button
              disabled={!file || status === 1 || status === 2}
              variant="contained"
              fullWidth
              sx={{ height: '2.5rem' }}
              onClick={onClickOk}
            >
              {status === 1 ? (
                <CircularProgress sx={{ height: '2.5rem' }} color="inherit" />
              ) : (
                t('BTN_OK')
              )}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
};
export default FileImportModal;
